import { useState } from "react"
import type { IssueApiKeyInput, IssuedApiKey, ToolCatalog } from "../api/admin-client.ts"
import { CopyRow } from "./CopyRow.tsx"
import { ScopeMatrix, scopeSatisfiesAny } from "./ScopeMatrix.tsx"

/**
 * API 키 발급 위저드 — 라벨/만료/역할 프리셋 + ScopeMatrix.
 *
 * 서버의 enabledTools 와 발급 스코프를 교차 검증:
 * - enabledTools 중 하나라도 requiredScopes 를 만족하는 스코프가 granted 에 포함되어야 유용한 키
 * - 그렇지 않으면 "호출 가능한 툴 없음" 경고 표시
 *
 * 발급 성공 시 평문 키를 1회 노출하고, "닫기" 시 목록 갱신 콜백 호출.
 */
export function IssueKeyWizard(props: {
	readonly slug: string
	readonly serverName: string
	readonly serverEnabledTools: ReadonlyArray<string>
	readonly catalog: ToolCatalog | undefined
	readonly onClose: () => void
	readonly onIssue: (input: IssueApiKeyInput) => Promise<IssuedApiKey>
}) {
	const [label, setLabel] = useState("")
	const [expiresAt, setExpiresAt] = useState("")
	const [preset, setPreset] = useState<string>("reader")
	const [scopes, setScopes] = useState<ReadonlyArray<string>>(() =>
		resolvePresetScopes(props.catalog, "reader")
	)
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | undefined>()
	const [issued, setIssued] = useState<IssuedApiKey | undefined>()

	function selectPreset(p: string) {
		setPreset(p)
		if (p === "custom") return
		setScopes(resolvePresetScopes(props.catalog, p))
	}

	function computeUsableToolCount(): number {
		if (!props.catalog) return 0
		return props.serverEnabledTools.filter((tool) => {
			const spec = props.catalog?.catalog[tool]
			if (!spec) return false
			return scopeSatisfiesAny(scopes, spec.requiredScopes)
		}).length
	}

	const usableTools = computeUsableToolCount()
	const hasWarning = scopes.length > 0 && usableTools === 0
	const canSubmit = scopes.length > 0 && !busy

	async function submit(e: React.FormEvent) {
		e.preventDefault()
		if (!canSubmit) return
		setBusy(true)
		setError(undefined)
		try {
			const expiresAtIso = normalizeExpiresAt(expiresAt)
			const result = await props.onIssue({
				slug: props.slug,
				serverName: props.serverName,
				scopes,
				...(label.trim() ? { label: label.trim() } : {}),
				...(expiresAtIso ? { expiresAt: expiresAtIso } : {})
			})
			setIssued(result)
		} catch (err) {
			setError((err as Error).message)
		} finally {
			setBusy(false)
		}
	}

	const presetKeys = props.catalog ? Object.keys(props.catalog.rolePresets) : []

	return (
		<dialog
			className="modal-backdrop"
			open
			onMouseDown={(e) => e.currentTarget === e.target && props.onClose()}
		>
			<div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
				{issued ? (
					<IssuedView issued={issued} onClose={props.onClose} />
				) : (
					<form onSubmit={submit}>
						<div className="modal-head">
							<h2>새 API 키 발급</h2>
							<p>
								<code className="inline">
									/mcp/{props.slug}/{props.serverName}
								</code>{" "}
								전용 키.
							</p>
						</div>

						<div className="modal-body">
							{error && (
								<div className="alert alert-danger" style={{ marginBottom: 14 }}>
									{error}
								</div>
							)}

							<div className="field">
								<label className="field-label" htmlFor="key-label">
									라벨 (선택)
								</label>
								<input
									id="key-label"
									className="input"
									placeholder="예: prod-agent-1"
									value={label}
									onChange={(e) => setLabel(e.target.value)}
									maxLength={128}
								/>
							</div>

							<div className="field">
								<label className="field-label" htmlFor="key-expires">
									만료일시 (선택)
								</label>
								<input
									id="key-expires"
									className="input"
									type="datetime-local"
									value={expiresAt}
									onChange={(e) => setExpiresAt(e.target.value)}
								/>
								<span className="field-hint">미지정 시 무기한.</span>
							</div>

							<div className="field">
								<label className="field-label" htmlFor="key-preset">
									역할 프리셋
								</label>
								<select
									id="key-preset"
									className="input select"
									value={preset}
									onChange={(e) => selectPreset(e.target.value)}
								>
									{presetKeys.map((key) => (
										<option key={key} value={key}>
											{key}
										</option>
									))}
									<option value="custom">custom</option>
								</select>
								<span className="field-hint">프리셋 선택 시 아래 스코프가 자동 갱신됩니다.</span>
							</div>

							<div className="field">
								<div className="field-label">스코프 ({scopes.length})</div>
								<ScopeMatrix
									value={scopes}
									onChange={(next) => {
										setScopes(next)
										setPreset("custom")
									}}
								/>
							</div>

							{hasWarning && (
								<div className="alert alert-warn" style={{ marginTop: 12 }}>
									이 서버의 활성 툴 중 선택한 scope 로 호출 가능한 것이 없습니다. 발급은 가능하지만 유용하지
									않을 수 있습니다.
								</div>
							)}
							{scopes.length > 0 && !hasWarning && (
								<div className="field-hint" style={{ marginTop: 10, color: "var(--success)" }}>
									호출 가능한 툴 {usableTools}개 / 전체 {props.serverEnabledTools.length}개
								</div>
							)}
						</div>

						<div className="modal-foot">
							<button type="button" className="btn btn-ghost" onClick={props.onClose} disabled={busy}>
								취소
							</button>
							<button type="submit" className="btn btn-primary" disabled={!canSubmit}>
								{busy ? "발급 중..." : "발급"}
							</button>
						</div>
					</form>
				)}
			</div>
		</dialog>
	)
}

function IssuedView(props: { readonly issued: IssuedApiKey; readonly onClose: () => void }) {
	return (
		<>
			<div className="modal-head">
				<h2>API 키 발급 완료</h2>
				<p>이 화면을 닫으면 평문 키는 다시 볼 수 없습니다.</p>
			</div>
			<div className="modal-body">
				<div className="alert alert-warn" style={{ marginBottom: 14 }}>
					아래 키는 이번 한 번만 표시됩니다. 안전한 곳에 저장하세요.
				</div>
				<CopyRow value={props.issued.plainKey} />
				<div className="kv" style={{ marginTop: 18 }}>
					<dt>키 ID</dt>
					<dd>
						<code className="inline">{props.issued.record.keyId}</code>
					</dd>
					<dt>라벨</dt>
					<dd>{props.issued.record.label || "—"}</dd>
					<dt>스코프</dt>
					<dd>
						<div className="chips">
							{props.issued.record.scopes.map((s) => (
								<span key={s} className="chip">
									{s}
								</span>
							))}
						</div>
					</dd>
					<dt>생성</dt>
					<dd className="muted">{new Date(props.issued.record.createdAt).toLocaleString("ko-KR")}</dd>
					{props.issued.record.expiresAt && (
						<>
							<dt>만료</dt>
							<dd className="muted">{new Date(props.issued.record.expiresAt).toLocaleString("ko-KR")}</dd>
						</>
					)}
				</div>
			</div>
			<div className="modal-foot">
				<button type="button" className="btn btn-primary" onClick={props.onClose}>
					닫기
				</button>
			</div>
		</>
	)
}

function resolvePresetScopes(catalog: ToolCatalog | undefined, preset: string): ReadonlyArray<string> {
	if (!catalog) return []
	return catalog.rolePresets[preset] ?? []
}

function normalizeExpiresAt(value: string): string | undefined {
	if (!value) return undefined
	// datetime-local 은 "YYYY-MM-DDTHH:MM" 형식 → Date 변환 후 ISO string
	const d = new Date(value)
	if (Number.isNaN(d.getTime())) return undefined
	return d.toISOString()
}
